import * as THREE from 'three';
import { CELL } from './Particles.js';

/**
 * Muzzle flash.
 *
 * A muzzle flash is a burning gas jet, not a white sprite. This one is assembled
 * from four things, and the last one matters most:
 *
 *  1. A star-shaped bloom in the plane of the muzzle, re-rolled every shot so no
 *     two flashes are identical, mirrored onto a second perpendicular plane so it
 *     still reads when you are stood beside the shooter.
 *  2. A short forward plume — the unburnt propellant still combusting past the
 *     crown — brightest at the barrel and fading out along its length.
 *  3. A hot white core small enough that bloom does the work of blowing it out.
 *  4. **A dynamic light.** The flash illuminating the wall beside you, the ground
 *     under you and the weapon in your hands is most of what sells the shot; the
 *     geometry is almost decoration by comparison. It decays over ~50 ms.
 *
 * The point lights are kept resident in the scene at zero intensity rather than
 * added and removed, because changing the number of lights in a three.js scene
 * triggers a shader recompile — and a recompile in the middle of a firefight is a
 * visible hitch.
 */

export const TUNING = {
  flashLife: 0.048,
  flashRise: 0.008,
  plumeLength: 0.42,
  plumeRadius: 0.11,
  starRadius: 0.20,
  coreRadius: 0.045,

  /**
   * Candela. `useLegacyLights` is off in r169, so this is a physical emitter:
   * `intensity / d²` lux at d metres. For scale, the sun in this level is a
   * DirectionalLight at 7.6 — 7.6 lux on every face-on surface, with no falloff.
   *
   * 24 cd therefore puts about three suns on a wall a metre from the barrel,
   * matches daylight at 1.8 m, and has dropped below the ambient fill by 7 m. The
   * old 42 cd over a 16 m radius was still contributing at the far end of the
   * street, which — with `castShadow` off — meant every shot lit the far side of
   * whatever the shooter was stood behind. Shadowing a muzzle flash is not an
   * option (a PointLight shadow is a cube map: six full renders of the level for
   * a flash that lasts 50 ms), so the leak is bounded by the radius instead.
   */
  lightIntensity: 24,
  lightDistance: 7,
  lightLife: 0.05,
  lightCount: 3,
  lightColor: 0xffd39a,

  smokePuffs: 5,
};

/* ========================================================================== *
 * Transient point lights
 * ========================================================================== */

export class FlashLights {
  constructor(ctx) {
    this.ctx = ctx;
    this.external = typeof ctx.lighting?.addPointLight === 'function' ? ctx.lighting : null;
    this.lights = [];
    this.state = [];
    this._pos = new THREE.Vector3();
    this._col = new THREE.Color();

    if (!this.external) {
      for (let i = 0; i < TUNING.lightCount; i++) {
        const l = new THREE.PointLight(0xffffff, 0, TUNING.lightDistance, 2);
        l.castShadow = false;
        l.visible = true; // resident: never toggled, so the program never recompiles
        ctx.scene.add(l);
        this.lights.push(l);
        this.state.push({ t: 0, life: 1, peak: 0 });
      }
      this.next = 0;
    }
  }

  /** Fire a light pulse. Colour is a hex in sRGB; intensity is in candela. */
  pulse(x, y, z, colorHex, intensity, distance, life) {
    if (this.external) {
      this._pos.set(x, y, z);
      this._col.setHex(colorHex);
      try {
        this.external.addPointLight(this._pos, this._col, intensity, distance, life);
      } catch (err) {
        console.warn('[fx] ctx.lighting.addPointLight rejected the call', err);
        this.external = null;
      }
      return;
    }
    if (!this.lights.length) return;
    const i = this.next;
    this.next = (this.next + 1) % this.lights.length;
    const l = this.lights[i];
    l.position.set(x, y, z);
    l.color.setHex(colorHex);
    l.distance = distance;
    l.intensity = intensity;
    const s = this.state[i];
    s.t = 0;
    s.life = life;
    s.peak = intensity;
  }

  update(dt) {
    for (let i = 0; i < this.lights.length; i++) {
      const s = this.state[i];
      if (s.peak <= 0) continue;
      s.t += dt;
      if (s.t >= s.life) {
        s.peak = 0;
        this.lights[i].intensity = 0;
        continue;
      }
      // Fast attack, cubic decay: the eye reads a flash by its falloff.
      const k = 1 - s.t / s.life;
      this.lights[i].intensity = s.peak * k * k * k;
    }
  }
}

/* ========================================================================== *
 * Flash geometry
 * ========================================================================== */

/** Irregular star in the XY plane, drawn as a fan with hot centre, dark rim. */
function makeStarGeometry(points, seed) {
  const pos = [];
  const col = [];
  let s = seed;
  const rnd = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
  const n = points * 2;
  const radii = [];
  for (let i = 0; i < n; i++) radii.push(i % 2 === 0 ? 0.75 + rnd() * 0.55 : 0.16 + rnd() * 0.16);
  for (let i = 0; i < n; i++) {
    const a0 = (i / n) * Math.PI * 2;
    const a1 = ((i + 1) / n) * Math.PI * 2;
    const r0 = radii[i];
    const r1 = radii[(i + 1) % n];
    pos.push(0, 0, 0, Math.cos(a0) * r0, Math.sin(a0) * r0, 0, Math.cos(a1) * r1, Math.sin(a1) * r1, 0);
    // Values well above 1: the flash is an HDR emitter and needs to blow out the
    // bloom pass, not sit politely at display white.
    col.push(5.5, 4.2, 2.4, 1.4, 0.42, 0.06, 1.4, 0.42, 0.06);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}

/** Forward gas plume: a cone along +Z, bright at the crown, dying at the tip. */
function makePlumeGeometry(radius, length) {
  const g = new THREE.ConeGeometry(radius, length, 9, 1, true);
  g.rotateX(Math.PI / 2);
  g.translate(0, 0, length * 0.5);
  const p = g.attributes.position;
  const col = new Float32Array(p.count * 3);
  for (let i = 0; i < p.count; i++) {
    const t = Math.max(0, Math.min(1, p.getZ(i) / length));
    const k = Math.pow(1 - t, 1.8);
    col[i * 3] = 3.4 * k;
    col[i * 3 + 1] = (1.5 + 1.0 * (1 - t)) * k;
    col[i * 3 + 2] = 0.35 * k;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/* ========================================================================== *
 * Muzzle
 * ========================================================================== */

export class Muzzle {
  constructor(ctx, worldRoot, particles, lights) {
    this.ctx = ctx;
    this.particles = particles;
    this.lights = lights;
    this.active = false;
    this.t = 0;
    this.life = TUNING.flashLife;

    const starMat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      toneMapped: true,
      fog: false,
    });
    const plumeMat = starMat.clone();
    const coreMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(3.2, 2.9, 2.4),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: true,
      fog: false,
    });
    this.materials = [starMat, plumeMat, coreMat];

    const build = () => {
      const g = new THREE.Group();
      const s1 = new THREE.Mesh(makeStarGeometry(6, 7717), starMat);
      s1.scale.setScalar(TUNING.starRadius);
      const s2 = new THREE.Mesh(makeStarGeometry(5, 991), starMat);
      s2.scale.setScalar(TUNING.starRadius * 0.8);
      s2.rotation.y = Math.PI / 2; // cross-billboard, so it survives side-on views
      const plume = new THREE.Mesh(makePlumeGeometry(TUNING.plumeRadius, TUNING.plumeLength), plumeMat);
      const core = new THREE.Mesh(new THREE.IcosahedronGeometry(TUNING.coreRadius, 1), coreMat);
      g.add(s1, s2, plume, core);
      g.visible = false;
      g.matrixAutoUpdate = true;
      g.frustumCulled = false;
      for (const c of g.children) {
        c.frustumCulled = false;
        c.renderOrder = 20;
      }
      return g;
    };

    // One rig per scene: the viewmodel's muzzle lives in view space, a world actor's
    // muzzle lives in world space, and we cannot know which until the event fires.
    this.viewRig = build();
    this.worldRig = build();
    ctx.viewScene.add(this.viewRig);
    worldRoot.add(this.worldRig);
    this.rig = this.worldRig;

    this._pos = new THREE.Vector3();
    this._quat = new THREE.Quaternion();
    this._scl = new THREE.Vector3();
    this._fwd = new THREE.Vector3();
    this._worldPos = new THREE.Vector3();
    this._baseScale = 1;
    this.lastWorldPos = new THREE.Vector3();
    this.lastWorldDir = new THREE.Vector3(0, 0, -1);
    this.lastTime = -10;
  }

  /**
   * @param {THREE.Matrix4} matrix muzzle transform; local -Z or +Z is the bore
   * @param {number} scale weapon-specific flash size multiplier
   */
  flash(matrix, scale = 1, elapsed = 0) {
    if (matrix) {
      matrix.decompose(this._pos, this._quat, this._scl);
    } else {
      this._pos.set(0, 0, 0);
      this._quat.identity();
    }

    // Which space is this matrix in? A viewmodel muzzle sits a few centimetres from
    // the view origin; a world muzzle sits a few centimetres from the camera. Test
    // both and prefer world space when the two are indistinguishable.
    const cam = this.ctx.camera;
    const camDist = this._pos.distanceTo(cam.position);
    const originDist = this._pos.length();
    const viewSpace = camDist > 2.0 && originDist <= 2.0;

    this.rig = viewSpace ? this.viewRig : this.worldRig;
    this.viewRig.visible = false;
    this.worldRig.visible = false;
    this.rig.visible = true;
    this.rig.position.copy(this._pos);
    this.rig.quaternion.copy(this._quat);
    // Fresh roll about the bore every shot; a repeating flash shape is instantly
    // recognisable as a canned sprite.
    this.rig.rotateZ(Math.random() * Math.PI * 2);

    this._baseScale = scale * (0.82 + Math.random() * 0.42);
    this.active = true;
    this.t = 0;
    this._apply(0);

    /* ---- world-space position for the light and the smoke ---- */
    this._worldPos.copy(this._pos);
    this._fwd.set(0, 0, 1).applyQuaternion(this._quat);
    if (viewSpace) {
      this._worldPos.applyMatrix4(cam.matrixWorld);
      this._fwd.transformDirection(cam.matrixWorld);
    }
    // The bore axis convention is unknown; take whichever of ±Z points away from
    // the camera, which is always the direction the weapon is firing.
    const away = this._worldPos.x - cam.position.x;
    const awayY = this._worldPos.y - cam.position.y;
    const awayZ = this._worldPos.z - cam.position.z;
    void away; void awayY; void awayZ;
    cam.getWorldDirection(_camDir);
    if (this._fwd.dot(_camDir) < 0) this._fwd.negate();

    this.lastWorldPos.copy(this._worldPos);
    this.lastWorldDir.copy(this._fwd);
    this.lastTime = elapsed;

    this.lights.pulse(
      this._worldPos.x + this._fwd.x * 0.2,
      this._worldPos.y + this._fwd.y * 0.2,
      this._worldPos.z + this._fwd.z * 0.2,
      TUNING.lightColor,
      TUNING.lightIntensity * scale,
      TUNING.lightDistance,
      TUNING.lightLife
    );

    this._smoke(scale);
  }

  _smoke(scale) {
    const p = this.particles;
    if (!p) return;
    const wp = this._worldPos;
    const f = this._fwd;
    // A little unburnt powder smoke pushed out of the crown, drifting up.
    for (let i = 0; i < TUNING.smokePuffs; i++) {
      const spread = 0.06;
      p.beginSoft()
        .pos(wp.x + (Math.random() - 0.5) * spread, wp.y + (Math.random() - 0.5) * spread, wp.z + (Math.random() - 0.5) * spread)
        .vel(
          f.x * (1.4 + Math.random() * 1.6) + (Math.random() - 0.5) * 0.5,
          f.y * (1.4 + Math.random() * 1.6) + 0.35 + Math.random() * 0.3,
          f.z * (1.4 + Math.random() * 1.6) + (Math.random() - 0.5) * 0.5
        )
        .life(0.45 + Math.random() * 0.7)
        .drag(3.4)
        .grav(-0.06)
        .size(0.035 * scale, (0.20 + Math.random() * 0.28) * scale)
        .roll(Math.random() * 6.28, (Math.random() - 0.5) * 2.0)
        // Powder smoke is mid-grey in daylight. At 0.62 the shader's own sun term
        // pushed it past a sunlit plaster wall, which put the brightest object in
        // the frame two feet from the player's eye on every single shot.
        .colA(0.34, 0.33, 0.32, 0.13 + Math.random() * 0.16)
        .colB(0.17, 0.165, 0.165)
        .atlas(CELL.SMOKE)
        .shade(1)
        .fade(0.12, 1.5)
        .go();
    }
    // A single bright expanding gas ring right at the crown.
    p.beginAdd()
      .pos(wp.x + f.x * 0.12, wp.y + f.y * 0.12, wp.z + f.z * 0.12)
      .vel(f.x * 0.4, f.y * 0.4, f.z * 0.4)
      .life(0.08)
      .drag(9)
      .grav(0)
      .size(0.06 * scale, 0.5 * scale)
      .roll(Math.random() * 6.28, 0)
      .colA(1.6, 0.9, 0.4, 0.7)
      .colB(0.6, 0.22, 0.06)
      .atlas(CELL.GLOW)
      .fade(0.02, 2.2)
      .go();
  }

  _apply(k) {
    // Snap open, collapse away. Real flashes are gone in two frames.
    const rise = Math.min(1, k / (TUNING.flashRise / TUNING.flashLife));
    const decay = Math.pow(1 - k, 1.7);
    const s = this._baseScale * (0.35 + 0.65 * rise) * (0.55 + 0.45 * decay);
    this.rig.scale.set(s * (0.9 + Math.random() * 0.2), s * (0.9 + Math.random() * 0.2), s);
    const o = decay;
    this.materials[0].opacity = o;
    this.materials[1].opacity = o * 0.85;
    this.materials[2].opacity = o;
  }

  update(dt) {
    if (!this.active) return;
    this.t += dt;
    const k = this.t / this.life;
    if (k >= 1) {
      this.active = false;
      this.viewRig.visible = false;
      this.worldRig.visible = false;
      return;
    }
    this._apply(k);
  }

  dispose() {
    for (const m of this.materials) m.dispose();
  }
}

const _camDir = new THREE.Vector3();
